import { useState, useEffect } from "react";

// Storage keys for upsell tracking
export enum UPSELL_STORAGE_KEYS {
  // Display tracking
  LAST_UPSELL_SHOWN = "ms-upsell-last-shown",
  DISMISSED_TRIGGERS = "ms-upsell-dismissed-triggers", // JSON object

  // Trigger tracking
  USE_CASE_CLICK_COUNT = "ms-upsell-use-case-clicks",
  CONTENT_CONSUMED_COUNT = "ms-upsell-content-consumed",
  SANDBOX_PROMPTS_DATA = "ms-upsell-sandbox-prompts", // JSON with timestamps
  AI_USAGE_LOG_COUNT = "ms-upsell-ai-usage-logs",

  // Navigation tracking
  LAST_USE_CASE_VISIT = "ms-upsell-last-use-case-visit",
  USE_CASE_TO_SANDBOX_TRIGGERED = "ms-upsell-use-case-to-sandbox-triggered", // timestamp when trigger was set
}

// SSR-safe local storage access functions
export function getStorageItem(key: string, defaultValue: string = ""): string {
  if (typeof window === "undefined") {
    return defaultValue;
  }
  return localStorage.getItem(key) || defaultValue;
}

export function setStorageItem(key: string, value: string): void {
  if (typeof window === "undefined") {
    return;
  }
  localStorage.setItem(key, value);
}

export function getStorageJSON<T>(key: string, defaultValue: T): T {
  if (typeof window === "undefined") {
    return defaultValue;
  }
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch {
    return defaultValue;
  }
}

export function setStorageJSON<T>(key: string, value: T): void {
  if (typeof window === "undefined") {
    return;
  }
  localStorage.setItem(key, JSON.stringify(value));
}

// Hook for client-side only storage operations
export function useClientStorage() {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  return {
    isClient,
    getItem: isClient ? getStorageItem : () => "",
    setItem: isClient ? setStorageItem : () => {},
    getJSON: isClient
      ? getStorageJSON
      : <T>(key: string, defaultValue: T) => defaultValue,
    setJSON: isClient ? setStorageJSON : () => {},
  };
}

// Utility function to clear all upsell-related storage (for rollback)
export function clearUpsellStorage(): void {
  if (typeof window === "undefined") {
    return;
  }

  Object.values(UPSELL_STORAGE_KEYS).forEach((key) => {
    localStorage.removeItem(key);
  });
}
